import StyledTreeItem from '@/components/treemenu/StyledTreeItem';
import { BackendApplication } from '@/types/backend';
import { Folder } from '@/types/folder';
import { flattenFolderAppTreeToIds } from '@/util/folder';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, LinearProgress, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { FC, useEffect, useRef, useState } from 'react';

type FolderTreeViewProps = {
  title?: string;
  folders: Folder[] | undefined;
  onFolderClick?: (folder: Folder) => void;
  onFolderDoubleClick?: (folder: Folder) => void;
  onApplicationClick?: (application: BackendApplication) => void;
  onApplicationDoubleClick?: (application: BackendApplication) => void;
  selectedItemId?: string;
  maxHeight?: number;
  foldersOnly?: boolean;
  disableFolderSelection?: boolean;
  disabledId?: number;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  required?: boolean;
  disabled?: boolean;
};

const FolderTreeView: FC<FolderTreeViewProps> = (props) => {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selectedItem, setSelectedItem] = useState(props.selectedItemId);

  const containerRef = useRef<HTMLDivElement>(null);

  const renderApplication = (application: BackendApplication) => {
    return (
      <StyledTreeItem
        sx={{
          borderLeft: '1px dotted lightgray'
        }}
        disabled={props.disabled}
        key={`app.${application.applicationId}`}
        itemId={`${application.applicationId}`}
        label={`${application.label} (${application.name})`}
        onClick={(e) => {
          e.stopPropagation();
          props.onApplicationClick && props.onApplicationClick(application);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          props.onApplicationDoubleClick && props.onApplicationDoubleClick(application);
        }}
      />
    );
  };

  const renderFolder = (folder: Folder) => {
    return (
      <StyledTreeItem
        key={`folder.${folder.folderId}`}
        itemId={`${folder.folderId}`}
        label={folder.name}
        disableSelection={props.disableFolderSelection}
        disabled={props.disabled || folder.folderId === props.disabledId}
        onClick={(e) => {
          e.stopPropagation();
          props.onFolderClick && props.onFolderClick(folder);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          props.onFolderDoubleClick && props.onFolderDoubleClick(folder);
        }}
      >
        {[
          ...(folder.folders?.map((subFolder) => renderFolder(subFolder)) || []),
          ...((!props.foldersOnly && folder.applications?.map((application) => renderApplication(application))) || [])
        ]}
      </StyledTreeItem>
    );
  };

  useEffect(() => {
    if (!props.selectedItemId) return;

    const scrollToSelected = () => {
      const selectedEl = containerRef.current?.querySelector('.Mui-selected');

      if (!selectedEl) return;

      selectedEl.scrollIntoView({
        behavior: 'instant',
        block: 'center'
      });
    };

    const animationFrame = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToSelected);
    });

    const timer = window.setTimeout(scrollToSelected, 600);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearTimeout(timer);
    };
  }, [props.selectedItemId, expanded]);

  useEffect(() => setSelectedItem(props.selectedItemId), [props.selectedItemId]);

  useEffect(() => {
    props.folders && setExpanded(flattenFolderAppTreeToIds(props.folders));
  }, []);

  return (
    <>
      {props.title && (
        <Typography variant='caption' color={props.error ? 'error.main' : 'inherit'} sx={{ pl: 1, mb: -2 }}>
          {props.title}
          {props.required && ' *'}
        </Typography>
      )}
      <Box ref={containerRef}>
        <SimpleTreeView
          slots={{ collapseIcon: ExpandMore, expandIcon: ChevronRight }}
          selectedItems={selectedItem}
          onItemSelectionToggle={(event, nodeId) => setSelectedItem(nodeId)}
          expandedItems={expanded}
          onExpandedItemsChange={(event, ids) => setExpanded(ids)}
          disableSelection={props.disabled}
          sx={{
            width: '100%',
            maxHeight: props.maxHeight || '20rem',
            overflowY: 'auto',
            border: '1px solid',
            borderColor: props.error ? 'error.main' : 'text.secondary',
            borderRadius: '0.25rem',
            padding: 1
          }}
        >
          {props.loading ? <LinearProgress /> : props.folders?.map((folder) => renderFolder(folder))}
        </SimpleTreeView>
      </Box>
      {props.helperText && (
        <Typography variant='caption' color={props.error ? 'error.main' : 'inherit'} sx={{ pl: 1, mt: -1.5 }}>
          {props.helperText}
        </Typography>
      )}
    </>
  );
};

export default FolderTreeView;
